import React, { useRef } from "react"
import { DndContext, PointerSensor, closestCenter, type DragEndEvent, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, SortableContext, rectSortingStrategy, useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import BookingPolicyTileIcon from "@/components/booking_policy_tile_icon"
import i18n from "@/i18n"
import {
  getBookingPolicyTileTitle,
  type BookingPolicyTileKind,
  type BookingPolicyTileViewModel,
} from "@/shared/booking_policy"
import { SETTINGS_SECTION_STYLES } from "@/components/settings/_styles"
import { DRAG_TOGGLE_SUPPRESSION_DISTANCE, TAILWIND_SM_BREAKPOINT, TAILWIND_LG_BREAKPOINT } from "./constants"
import BookingPolicyPanel from "./booking_policy_panel"

const STYLES = {
  ...SETTINGS_SECTION_STYLES,
  tileGrid: "grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3",
  tileWrapper: "touch-none",
  tileButton:
    "group relative flex w-full cursor-grab items-start gap-3 rounded-lg p-0 text-left transition-[opacity,box-shadow] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2da0fe] active:cursor-grabbing",
  tileButtonEnabled: "hover:opacity-80",
  tileButtonDisabled: "opacity-40",
  tileIcon: "flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] bg-[#f5f7f9] text-[#6b7686]",
  tileTitle: "text-[12px] font-medium leading-[17px] text-black",
  tileSubtitle: "mt-0 text-[10px] leading-[14px] text-[#616161]",
  tileAction:
    "pointer-events-none absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#ff3b30] opacity-0 shadow-[0_4px_10px_rgba(0,0,0,0.18)] transition-opacity group-hover:opacity-100",
  tileIconImage: "h-[18px] w-[18px]",
  tileContent: "min-w-0",
  tileActionIcon: "h-2.5 w-2.5",
  headerRow: "flex items-start justify-between",
  previewLabel: "shrink-0 text-xs text-gray-400",
} as const

const getGridColumnCount = () => {
  if (typeof window === "undefined") {
    return 1
  }

  if (window.innerWidth >= TAILWIND_LG_BREAKPOINT) {
    return 3
  }

  if (window.innerWidth >= TAILWIND_SM_BREAKPOINT) {
    return 2
  }

  return 1
}

type BookingPolicyPreviewProps = {
  tiles: BookingPolicyTileViewModel[]
  content?: string | null
  sectionTitle?: string | null
  onChange: (tiles: BookingPolicyTileViewModel[]) => void
}

type SortableTileProps = {
  tile: BookingPolicyTileViewModel
  onToggle: (kind: BookingPolicyTileKind) => void
  onKeyReorder: (kind: BookingPolicyTileKind, offset: number) => void
}

const SortableTile: React.FC<SortableTileProps> = ({ tile, onToggle, onKeyReorder }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tile.kind })
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const suppressClickRef = useRef(false)

  const startTracking = (x: number, y: number) => {
    pointerStartRef.current = { x, y }
    suppressClickRef.current = false
  }

  const trackMovement = (x: number, y: number) => {
    if (!pointerStartRef.current) {
      return
    }

    const distance = Math.hypot(x - pointerStartRef.current.x, y - pointerStartRef.current.y)

    if (distance >= DRAG_TOGGLE_SUPPRESSION_DISTANCE) {
      suppressClickRef.current = true
    }
  }

  const stopTracking = () => {
    pointerStartRef.current = null
  }

  return (
    <div
      ref={setNodeRef}
      className={STYLES.tileWrapper}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : undefined,
        zIndex: isDragging ? 1 : undefined,
      }}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        onMouseDownCapture={(event) => {
          startTracking(event.clientX, event.clientY)
        }}
        onMouseMoveCapture={(event) => {
          trackMovement(event.clientX, event.clientY)
        }}
        onMouseUpCapture={() => {
          stopTracking()
        }}
        onTouchStartCapture={(event) => {
          const touch = event.touches[0]

          if (touch) {
            startTracking(touch.clientX, touch.clientY)
          }
        }}
        onTouchMoveCapture={(event) => {
          const touch = event.touches[0]

          if (touch) {
            trackMovement(touch.clientX, touch.clientY)
          }
        }}
        onTouchEndCapture={() => {
          stopTracking()
        }}
        onTouchCancelCapture={() => {
          stopTracking()
          suppressClickRef.current = false
        }}
        onClick={() => {
          if (suppressClickRef.current) {
            suppressClickRef.current = false
            stopTracking()
            return
          }

          stopTracking()
          onToggle(tile.kind)
        }}
        onKeyDown={(event) => {
          suppressClickRef.current = false

          const columnCount = getGridColumnCount()

          if (!event.altKey) return

          if (event.key === "ArrowLeft") {
            event.preventDefault()
            onKeyReorder(tile.kind, -1)
          }

          if (event.key === "ArrowRight") {
            event.preventDefault()
            onKeyReorder(tile.kind, 1)
          }

          if (event.key === "ArrowUp") {
            event.preventDefault()
            onKeyReorder(tile.kind, -columnCount)
          }

          if (event.key === "ArrowDown") {
            event.preventDefault()
            onKeyReorder(tile.kind, columnCount)
          }
        }}
        className={`${STYLES.tileButton} ${tile.enabled ? STYLES.tileButtonEnabled : STYLES.tileButtonDisabled}`.trim()}
        aria-pressed={tile.enabled}
        aria-keyshortcuts="Alt+ArrowLeft Alt+ArrowRight Alt+ArrowUp Alt+ArrowDown"
        data-testid={`settings-booking-policy-tile-${tile.kind}`}
      >
        <span className={STYLES.tileIcon}>
          <BookingPolicyTileIcon kind={tile.kind} className={STYLES.tileIconImage} />
        </span>

        <div className={STYLES.tileContent}>
          <div className={STYLES.tileTitle}>{getBookingPolicyTileTitle(tile.kind)}</div>
          <div className={STYLES.tileSubtitle}>{tile.subtitle}</div>
        </div>

        <span className={STYLES.tileAction} aria-hidden="true">
          <svg className={STYLES.tileActionIcon} fill="none" viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 2L8 8M8 2L2 8" stroke="currentColor" strokeLinecap="round" strokeWidth="1.4" />
          </svg>
        </span>
      </button>
    </div>
  )
}

const BookingPolicyPreview: React.FC<BookingPolicyPreviewProps> = ({ tiles, content, sectionTitle, onChange }) => {
  const hasContent = Boolean(content?.trim())
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  const handleKeyReorder = (kind: BookingPolicyTileKind, offset: number) => {
    const currentIndex = tiles.findIndex((tile) => tile.kind === kind)
    const nextIndex = currentIndex + offset

    if (currentIndex === -1 || nextIndex < 0 || nextIndex >= tiles.length) {
      return
    }

    onChange(arrayMove(tiles, currentIndex, nextIndex))
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = tiles.findIndex((tile) => tile.kind === active.id)
    const newIndex = tiles.findIndex((tile) => tile.kind === over.id)

    onChange(arrayMove(tiles, oldIndex, newIndex))
  }

  const handleToggle = (kind: BookingPolicyTileKind) => {
    onChange(tiles.map((tile) => (tile.kind === kind ? { ...tile, enabled: !tile.enabled } : tile)))
  }

  return (
    <section className={STYLES.container} data-testid="settings-venue-rules-booking-policy-preview">
      <div className={STYLES.headerRow}>
        <h2 className={STYLES.header}>
          {sectionTitle || i18n.t("app.settings.venues.reservation_rules.booking_policy.title")}
        </h2>
        <span className={STYLES.previewLabel}>{i18n.t("app.settings.venues.reservation_rules.preview")}</span>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={tiles.map((tile) => tile.kind)} strategy={rectSortingStrategy}>
          <div className={STYLES.tileGrid} data-testid="settings-booking-policy-tiles">
            {tiles.map((tile) => (
              <SortableTile key={tile.kind} tile={tile} onToggle={handleToggle} onKeyReorder={handleKeyReorder} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {hasContent && <BookingPolicyPanel content={content!} />}

      <p className={STYLES.helperText}>{i18n.t("app.settings.venues.reservation_rules.booking_policy.description")}</p>
    </section>
  )
}

export default BookingPolicyPreview
